import React, { useState } from 'react';
import { LogIn, LogOut, Settings, Loader2, RefreshCw, Monitor, Smartphone, HelpCircle, Columns, LayoutList, Users, Share2, AlertTriangle, History, BarChart3, Shield, Zap, Mic } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import MoodPicker from './MoodPicker';
import { MOOD_THEMES } from '../../constants/mediscribe';
import { BrandEmblem } from '../BrandEmblem';
import ConnectionBadge from './ConnectionBadge';
import { persistProfilePref } from '../../utils/persistProfilePref';

const HeaderMobile = ({
  textSizeLevel,
  setTextSizeLevel,
  currentMood,
  currentMoodIndex,
  setCurrentMoodIndex,
  favoriteMoods,
  setFavoriteMoods,
  setShowMoodInfo,
  shareCode,
  setShowShareModal,
  historyShareCode,
  isSharing,
  user,
  setShowAuthModal,
  logout,
  userRole,
  switchRole,
  forceSingleColumn,
  toggleColumnLayout,
  pendingDraftsCount,
  onShowDrafts,
  onShowAllHistory,
  onShowTutorial,
  onShowBatchCharting,
  isAdmin,
  forceDesktopView,
  setForceDesktopView,
}) => {
  const navigate = useNavigate();
  const [showMobileSettings, setShowMobileSettings] = useState(false);

  return (
    <div className="sm:hidden">
      {/* Row 1: Logo + Auth */}
      <div className="flex items-center justify-between mb-0.5">
        <div style={{ '--brand-text-color': 'var(--foreground)' }} data-testid="app-logo" 
          className="transform scale-75 origin-left cursor-pointer" onClick={switchRole} title="Return to main page">
          <BrandEmblem size="sm" />
        </div>
        <div className="flex items-center gap-1.5">
          <ConnectionBadge hideWhenOnline compact />
          {userRole && (
            <button
              onClick={switchRole}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all active:scale-90 border ${
                userRole === 'provider' 
                  ? 'bg-primary/30 text-primary border-primary/50' 
                  : 'bg-green-500/30 text-green-400 border-green-500/50'
              }`}
              data-testid="role-indicator-mobile"
            >
              {userRole === 'provider' ? '\u{1FA7A}' : '\u{1F4CB}'}
              <RefreshCw className="w-3 h-3 opacity-60" />
            </button>
          )}
          {user ? (
            <Button variant="outline" size="sm" onClick={logout} className="h-7 px-2 text-xs border-red-500/30 text-red-400">
              <LogOut className="w-3 h-3" />
            </Button>
          ) : (
            <Button variant="default" size="sm" onClick={() => setShowAuthModal(true)} className="h-7 text-xs px-2 bg-green-600 hover:bg-green-700">
              <LogIn className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Row 2: Speed | Settings | History | MOOD | Share | Speed | Teams */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          {onShowBatchCharting && (
            <Button variant="ghost" size="sm" onClick={onShowBatchCharting}
              className="h-7 w-7 p-0 text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
              data-testid="speed-chart-btn-left" title="Speed Chart">
              <Zap className="w-4 h-4" />
            </Button>
          )}
          {/* Drive-Home Mode launcher (mobile) */}
          {userRole === 'provider' && (
            <a
              href="/drive"
              className="h-7 w-7 p-0 inline-flex items-center justify-center text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 rounded-md transition-colors"
              data-testid="drive-home-launcher-mobile"
              title="Drive-Home Mode"
            >
              <Mic className="w-4 h-4" />
            </a>
          )}
          <Sheet open={showMobileSettings} onOpenChange={setShowMobileSettings}>
            <Button variant="ghost" size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-primary"
              data-testid="mobile-settings-btn"
              onClick={() => setShowMobileSettings(true)}>
              <Settings className="w-4 h-4" />
            </Button>
            <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl">
              <SheetHeader className="pb-2">
                <SheetTitle className="text-sm">Quick Settings</SheetTitle>
              </SheetHeader>
              <div className="space-y-3 pb-6">
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground font-medium">Theme</label>
                  {userRole === 'provider' ? (
                    <MoodPicker currentMood={currentMood} currentMoodIndex={currentMoodIndex}
                      setCurrentMoodIndex={setCurrentMoodIndex} favoriteMoods={favoriteMoods}
                      setFavoriteMoods={setFavoriteMoods} setShowMoodInfo={setShowMoodInfo} />
                  ) : (
                    <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1">
                      <button onClick={() => { const i = MOOD_THEMES.findIndex(m => m.id === 'clinical'); if (i >= 0) setCurrentMoodIndex(i); }}
                        className={`flex-1 px-3 py-2.5 rounded text-xs font-medium transition-all ${currentMood?.id === 'clinical' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}
                        data-testid="theme-light-btn">
                        Light
                      </button>
                      <button onClick={() => { const i = MOOD_THEMES.findIndex(m => m.id === 'calm'); if (i >= 0) setCurrentMoodIndex(i); }}
                        className={`flex-1 px-3 py-2.5 rounded text-xs font-medium transition-all ${currentMood?.id === 'calm' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}
                        data-testid="theme-dark-btn">
                        Dark
                      </button>
                      <button onClick={() => { const i = MOOD_THEMES.findIndex(m => m.id === 'high-contrast'); if (i >= 0) setCurrentMoodIndex(i); }}
                        className={`flex-1 px-3 py-2.5 rounded text-xs font-medium transition-all ${currentMood?.id === 'high-contrast' ? 'bg-blue-500 text-white shadow-sm' : 'text-muted-foreground'}`}
                        data-testid="theme-high-contrast-btn">
                        Hi-Con
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground font-medium">Text Size</label>
                  <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-1">
                    {['Normal', 'Large', 'X-Large'].map((label, idx) => (
                      <button key={label} onClick={() => setTextSizeLevel(idx)}
                        className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${textSizeLevel === idx ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground font-medium">View Mode</label>
                  <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-1">
                    <button onClick={() => { setForceDesktopView(false); localStorage.setItem('writeright_force_desktop', 'false'); persistProfilePref({ force_desktop_view: false }); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-all ${!forceDesktopView ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
                      <Smartphone className="w-3.5 h-3.5" /> Mobile
                    </button>
                    <button onClick={() => { setForceDesktopView(true); localStorage.setItem('writeright_force_desktop', 'true'); persistProfilePref({ force_desktop_view: true }); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-all ${forceDesktopView ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
                      <Monitor className="w-3.5 h-3.5" /> Desktop
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-xs text-muted-foreground font-medium">Layout</label>
                  <div className="flex items-center gap-2 bg-secondary/50 rounded-lg p-1">
                    <button onClick={() => { if (forceSingleColumn) toggleColumnLayout(); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-all ${!forceSingleColumn ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
                      <Columns className="w-3.5 h-3.5" /> 2 Column
                    </button>
                    <button onClick={() => { if (!forceSingleColumn) toggleColumnLayout(); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-all ${forceSingleColumn ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
                      <LayoutList className="w-3.5 h-3.5" /> 1 Column
                    </button>
                  </div>
                </div>
                
                <div className="pt-2 border-t border-border space-y-2">
                  {onShowAllHistory && (
                    <button onClick={() => { setShowMobileSettings(false); onShowAllHistory(); }}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium bg-secondary/50 text-foreground hover:bg-secondary transition-all">
                      <History className="w-3.5 h-3.5" /> See All History
                    </button>
                  )}
                  {onShowBatchCharting && (
                    <button onClick={() => { setShowMobileSettings(false); onShowBatchCharting(); }}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium text-foreground transition-all"
                      style={{ background: 'linear-gradient(180deg, rgba(255,215,0,0.15) 0%, rgba(218,165,32,0.1) 100%)', border: '1px solid rgba(255,215,0,0.3)' }}
                      data-testid="speed-chart-btn">
                      <Zap className="w-3.5 h-3.5 text-amber-500" /> Speed Chart (4 Patients)
                    </button>
                  )}
                  {user && (
                    <button onClick={() => { setShowMobileSettings(false); navigate('/dashboard'); }}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium bg-secondary/50 text-foreground hover:bg-secondary transition-all"
                      data-testid="mobile-dashboard-btn">
                      <BarChart3 className="w-3.5 h-3.5" /> My Productivity Stats
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => { setShowMobileSettings(false); navigate('/admin'); }}
                      className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-all"
                      data-testid="mobile-admin-btn">
                      <Shield className="w-3.5 h-3.5" /> Admin Portal
                    </button>
                  )}
                  <button onClick={() => {
                      if ('serviceWorker' in navigator) { navigator.serviceWorker.getRegistrations().then(r => r.forEach(reg => reg.update())); }
                      if ('caches' in window) { caches.keys().then(n => n.forEach(name => caches.delete(name))); }
                      window.location.reload(true);
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium bg-primary/20 text-primary hover:bg-primary/30 transition-all">
                    <RefreshCw className="w-3.5 h-3.5" /> Update App
                  </button>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setShowMobileSettings(false); onShowTutorial && onShowTutorial(); }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium text-muted-foreground hover:text-primary hover:bg-secondary/50 transition-all">
                      <HelpCircle className="w-3.5 h-3.5" /> Help
                    </button>
                    <button onClick={() => {
                        if (window.confirm('Clear LOCAL settings on this device?\n\nYour history (if logged in) stays on the server.')) {
                          localStorage.clear(); sessionStorage.clear(); window.location.href = window.location.origin;
                        }
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium text-amber-500 hover:bg-amber-500/10 transition-all">
                      <AlertTriangle className="w-3.5 h-3.5" /> Reset Local
                    </button>
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          
          <Button variant="ghost" size="sm" onClick={onShowAllHistory}
            className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" data-testid="mobile-history-btn">
            <History className="w-4 h-4" />
          </Button>
        </div>
        
        {userRole === 'provider' && (
          <MoodPicker currentMood={currentMood} currentMoodIndex={currentMoodIndex}
            setCurrentMoodIndex={setCurrentMoodIndex} favoriteMoods={favoriteMoods}
            setFavoriteMoods={setFavoriteMoods} setShowMoodInfo={setShowMoodInfo} />
        )}
        
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)} disabled={isSharing}
            className={`h-7 w-7 p-0 ${shareCode ? 'text-green-500' : historyShareCode ? 'text-blue-500' : 'text-muted-foreground hover:text-primary'}`}
            data-testid="mobile-share-btn">
            {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          </Button>
          {onShowBatchCharting && (
            <Button variant="ghost" size="sm" onClick={onShowBatchCharting}
              className="h-7 w-7 p-0 text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
              data-testid="speed-chart-btn-right" title="Speed Chart">
              <Zap className="w-4 h-4" />
            </Button>
          )}
          {user && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}
              className="h-7 w-7 p-0 text-muted-foreground hover:text-primary" data-testid="mobile-teams-btn">
              <Users className="w-4 h-4" />
            </Button>
          )}
          {userRole === 'provider' && pendingDraftsCount > 0 && (
            <Button variant="default" size="sm" className="h-6 px-1.5 text-[10px] bg-blue-600"
              onClick={() => onShowDrafts && onShowDrafts()}>
              {pendingDraftsCount}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeaderMobile;
